import { useState, useEffect, useCallback } from 'react';

const EMPTY_TRANSLATIONS = {
  en: { name: '', description: '', isAutoTranslated: false },
  es: { name: '', description: '', isAutoTranslated: false },
  ar: { name: '', description: '', isAutoTranslated: false },
};

/**
 * Custom hook that manages all translation-related form state.
 * Extracts ~8 useState variables and several helpers from Admin.js.
 *
 * @param {string} itemName  - current add-form item name (for base-lang sync)
 * @param {string} itemDescription - current add-form item description
 * @returns {object} Translation state, setters, and utility methods
 */
export default function useTranslationForm(itemName, itemDescription) {
  const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'English', nativeName: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
    { code: 'es', name: 'Spanish', nativeName: 'Espa\u00F1ol', flag: '\u{1F1EA}\u{1F1F8}' },
    { code: 'ar', name: 'Arabic', nativeName: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629', flag: '\u{1F1F8}\u{1F1E6}', rtl: true },
  ];

  const [baseLang, setBaseLang] = useState('en');
  const [activeTab, setActiveTab] = useState('en');
  const [translations, setTranslations] = useState({ ...EMPTY_TRANSLATIONS });
  const [translating, setTranslating] = useState(false);
  const [bulkTranslateOpen, setBulkTranslateOpen] = useState(false);
  const [bulkTranslating, setBulkTranslating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResults, setBulkResults] = useState(null);

  /** Keep base language in sync with the add form's name/description */
  useEffect(() => {
    setTranslations(prev => ({
      ...prev,
      [baseLang]: {
        ...prev[baseLang],
        name: itemName,
        description: itemDescription,
        isAutoTranslated: false,
      },
    }));
  }, [itemName, itemDescription, baseLang]);

  /** Update a single field in a single language */
  const updateTranslation = useCallback((langCode, field, value) => {
    setTranslations(prev => ({
      ...prev,
      [langCode]: {
        ...prev[langCode],
        [field]: value,
        isAutoTranslated: field === 'isAutoTranslated' ? value : false,
      },
    }));
  }, []);

  /** Reset all translation fields */
  const resetTranslations = useCallback(() => {
    setTranslations({
      en: { name: '', description: '', isAutoTranslated: false },
      es: { name: '', description: '', isAutoTranslated: false },
      ar: { name: '', description: '', isAutoTranslated: false },
    });
    setActiveTab(baseLang);
  }, [baseLang]);

  return {
    SUPPORTED_LANGUAGES,
    baseLang, setBaseLang,
    activeTab, setActiveTab,
    translations, setTranslations,
    translating, setTranslating,
    bulkTranslateOpen, setBulkTranslateOpen,
    bulkTranslating, setBulkTranslating,
    bulkProgress, setBulkProgress,
    bulkResults, setBulkResults,
    updateTranslation,
    resetTranslations,
  };
}
